import React, { createContext, ReactNode, useContext } from 'react';

// We define different log levels to give more control over logging
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Define the shape of our debugger context
interface DebuggerContextType {
  log: (component: string, message: string, level?: LogLevel) => void;
  group: (component: string, message: string) => void;
  groupEnd: () => void;
}

export const DebuggerContext = createContext<DebuggerContextType | undefined>(
  undefined,
);

interface DebuggerProviderProps {
  children: ReactNode;
}

export const DebuggerProvider: React.FC<DebuggerProviderProps> = ({
  children,
}) => {
  // Utility to format timestamps consistently
  const getTimestamp = () => {
    return new Date().toISOString().split('T')[1].slice(0, -1);
  };

  // Format the log message with consistent styling
  const formatMessage = (component: string, message: string) => {
    return `[${getTimestamp()}] [${component}] ${message}`;
  };

  const log = (
    component: string,
    message: string,
    level: LogLevel = 'info',
  ) => {
    const formattedMessage = formatMessage(component, message);

    switch (level) {
      case 'debug':
        console.debug('%c' + formattedMessage, 'color: #666666');
        break;
      case 'info':
        console.info('%c' + formattedMessage, 'color: #2563eb');
        break;
      case 'warn':
        console.warn('%c' + formattedMessage, 'color: #ca8a04');
        break;
      case 'error':
        console.error('%c' + formattedMessage, 'color: #dc2626');
        break;
    }
  };

  // Helper for grouping related logs
  const group = (component: string, message: string) => {
    console.group(formatMessage(component, message));
  };

  const groupEnd = () => {
    console.groupEnd();
  };

  return (
    <DebuggerContext.Provider
      value={{
        log,
        group,
        groupEnd,
      }}
    >
      {children}
    </DebuggerContext.Provider>
  );
};

// Custom hook for using the debugger
export const useDebugger = () => {
  const context = useContext(DebuggerContext);
  if (context === undefined) {
    throw new Error('useDebugger must be used within a DebuggerProvider');
  }
  return context;
};
